import isEmpty from 'lodash/isEmpty';
import {
  clearCurrentUser,
  fetchCurrentUser,
  setCUserCurrencyAndLocation,
} from './user.duck';
import {
  createUserWithIdp,
  sendSMSNotification,
  sendSchedularMailFromSendGrid,
} from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import moment from 'moment';
import {
  clearCartFromSession,
  getCartFromSession,
} from '../util/sessionStorageManager';
import {
  fetchCartItems,
  getItemsByAuthor,
  setItemsSuccess,
} from '../ducks/cart.duck';
import { IS_DEVELOPMENT_ENV } from '../util/constants';
import { clearMixpanelIdentification, identifyUser } from '../util/mixpanel';
// import { initiateIntercomForLoggnedUser } from '../util/intercom';

const hourMailTemplateId =
  process.env.REACT_APP_SENDGRID_TEMP_THANKS_FOR_JOINING;
const v2WelcomeSmsContentSid =
  process.env.REACT_APP_TWILIO_v2_welcome_en;
const v2NeedAHandSmsContentSid =
  process.env.REACT_APP_TWILIO_v2_need_a_hand_en;

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;
const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

export const CLEAR_ERRORS = 'app/auth/CLEAR_ERRORS';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return {
        ...state,
        logoutInProgress: true,
        loginError: null,
        logoutError: null,
      };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        authScopes: [],
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return {
        ...state,
        signupInProgress: true,
        loginError: null,
        signupError: null,
      };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return {
        ...state,
        confirmInProgress: true,
        loginError: null,
        confirmError: null,
      };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case CLEAR_ERRORS:
      return {
        ...state,
        loginError: null,
        logoutError: null,
        signupError: null,
        confirmError: null,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({
  type: AUTH_INFO_SUCCESS,
  payload: info,
});

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({
  type: LOGIN_ERROR,
  payload: error,
  error: true,
});

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({
  type: LOGOUT_ERROR,
  payload: error,
  error: true,
});

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = currentUser => ({
  type: SIGNUP_SUCCESS,
  payload: currentUser,
});
export const signupError = error => ({
  type: SIGNUP_ERROR,
  payload: error,
  error: true,
});

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({
  type: CONFIRM_ERROR,
  payload: error,
  error: true,
});

export const clearErrors = () => ({ type: CLEAR_ERRORS });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(loginSuccess()))
    .then(() => dispatch(fetchCurrentUser()))
    .then(currentUser => {
      dispatch(setCUserCurrencyAndLocation(currentUser));

      // Get cart items from session storage
      const sessionCart = getCartFromSession() || [];

      if (sessionCart.length > 0) {
        // Get current user's profile cart
        const currentUserCart =
          currentUser?.attributes?.profile?.publicData?.cart || [];
        // Filter out items that belong to current user
        const itemsToAdd = sessionCart.filter(
          item => item.authorId !== currentUser.id.uuid
        );

        if (itemsToAdd.length > 0) {
          // Combine existing cart with new items
          let updatedCart = [...currentUserCart];

          itemsToAdd.forEach(item => {
            const existingItemIndex = updatedCart.findIndex(cartItem => {
              // Handle both bookingDates object and direct date properties
              const itemStart = item.bookingDates?.startDate;
              const itemEnd = item.bookingDates?.endDate;
              const cartItemStart = cartItem.bookingDates?.startDate;
              const cartItemEnd = cartItem.bookingDates?.endDate;

              return (
                item.listingId === cartItem.listingId &&
                item.package === cartItem.package &&
                moment(itemStart).isSame(cartItemStart) &&
                moment(itemEnd).isSame(cartItemEnd)
              );
            });

            if (existingItemIndex !== -1) {
              updatedCart[existingItemIndex] = {
                ...updatedCart[existingItemIndex],
                quantity: updatedCart[existingItemIndex].quantity + item.quantity,
                ...item,
                // Preserve the existing timestamp, or use item's timestamp if it has one
                addedAt: updatedCart[existingItemIndex].addedAt || item.addedAt || new Date().toISOString(),
              };
            } else {
              // Ensure new items have a timestamp
              updatedCart.push({
                ...item,
                addedAt: item.addedAt || new Date().toISOString(),
              });
            }
          });

          // Update user's profile with new cart
          return sdk.currentUser
            .updateProfile({
              publicData: {
                cart: updatedCart,
              },
            })
            .then(() => {
              // Clear session storage cart
              clearCartFromSession();

              // Update Redux state with new cart items
              return getItemsByAuthor(updatedCart, sdk).then(
                ({ itemsByAuthor, totalItems }) => {
                  dispatch(setItemsSuccess(itemsByAuthor, totalItems));
                  return currentUser;
                }
              );
            });
        }

        clearCartFromSession();
      }

      // Fetch cart items
      dispatch(fetchCartItems());
      return currentUser;
    })
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());

      // Clear Mixpanel identification
      clearMixpanelIdentification();

      // Shutdown Intercom and restart for unauthenticated user
      if (window && window.Intercom) {
        window.Intercom('shutdown');
        window.Intercom('boot', {
          app_id: intercomAppId,
        });
      }
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const {
    email,
    password,
    firstName,
    lastName,
    country,
    location,
    storeName,
    unit,
    isFromCheckout = false,
    ...rest
  } = params;

  const { address, city, origin, postcode, state } =
    location?.selectedPlace || {};

  const addressMaybe = !!address ? { address } : {};
  const cityMaybe = !!city ? { city } : {};
  const originMaybe = !!origin
    ? { origin: { lat: origin.lat, lng: origin.lng } }
    : {};
  const postcodeMaybe = !!postcode ? { postcode } : {};
  const storeNameMaybe = !!storeName?.trim() ? { storeName } : {};
  const unitMaybe = !!unit?.trim() ? { unit } : {};
  const stateMaybe = !!state ? { state } : {};
  const createUserParams = isEmpty(rest)
    ? { email, password, firstName, lastName }
    : {
      email,
      password,
      firstName,
      lastName,
      publicData: {
        country,
        ...addressMaybe,
        ...cityMaybe,
        ...originMaybe,
        ...postcodeMaybe,
        ...storeNameMaybe,
        ...unitMaybe,
        ...stateMaybe,
        permitted: false,
        isFromCheckout,
      },
      protectedData: { ...rest },
    };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(() => dispatch(signupSuccess({ firstName, lastName, email })))
    .then(() => dispatch(login(email, password)))
    .then(() => {
      console.log('Mailing Session');
      if (!isFromCheckout) { // Skip this check as the user is doing checkout
        const { currentUser } = getState().user;
        const {
          id: { uuid: currentUserId = '' },
        } = currentUser || {};
        const {
          phoneNumber,
          notificationSettings,
        } = currentUser.attributes.profile.protectedData;
        const { accountActivitySms = true } = notificationSettings || {};
        // Schedule 1 mails
        // 1. 5 minutes after signup (if no activity)
        const time5Minutes = moment()
          .add(6, 'minutes')
          .unix();

        sendSchedularMailFromSendGrid({
          templateID: hourMailTemplateId,
          email: email,
          setInCookie: true,
          userId: currentUserId,
          timeAt: time5Minutes,
          isToCC: true,
          data: {
            user_name: firstName,
          },
        });

        if (phoneNumber && accountActivitySms && !IS_DEVELOPMENT_ENV) {
          // Send welcome SMS
          sendSMSNotification({
            to: phoneNumber,
            contentSid: v2WelcomeSmsContentSid,
            contentVariables: { '1': firstName || 'there' },
          });
          // Send 5 minutes after signup SMS
          sendSMSNotification({
            to: phoneNumber,
            contentSid: v2NeedAHandSmsContentSid,
            sendAt: time5Minutes,
            contentVariables: { '1': firstName || 'there' },
          });
        }
      }
      return { success: true, user: { firstName, lastName, email } };
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

export const isAuthenticatedSelector = state => state.auth.isAuthenticated;
